import { ElMessage, ElMessageBox } from "element-plus";
import { createStore } from "vuex";
import { authApi, dbmetaApi, flowApi, jobApi, ruleApi, stressApi, toolsApi } from "../api";
import { getAuthToken, setAuthToken } from "../api/core";
import { extractLoginCredentials, isLoginJob, isVerifyCodeJob } from "../utils/session-headers";

import {
  DB_RULE_OPERATORS,
  HTTP_TYPES,
  blankFlowForm,
  blankJobForm,
  blankRuleAssertion,
  blankRuleForm,
  buildRuleText,
  flowPayload,
  formFromCurlJob,
  formFromJob,
  jobPayload,
  mergeFlowSteps,
  normalizeSteps,
  ruleFormFromRule,
} from "./form-helpers";

const ACTIVE_VIEW_STORAGE_KEY = "testjob.activeView";
const ACTIVE_VIEWS = new Set(["jobs", "sharedJobs", "sharedRules", "flows", "rules", "nodes"]);

function normalizeActiveView(view) {
  return view === "templates" ? "sharedJobs" : view;
}

function storedActiveView() {
  if (typeof window === "undefined") {
    return "jobs";
  }
  try {
    const view = normalizeActiveView(window.localStorage.getItem(ACTIVE_VIEW_STORAGE_KEY));
    return ACTIVE_VIEWS.has(view) ? view : "jobs";
  } catch (error) {
    return "jobs";
  }
}

function persistActiveView(view) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, view);
  } catch (error) {
    // Ignore storage failures; navigation still works for the current session.
  }
}

function downloadTextFile(filename, content) {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// Keep confirmation behavior in the store because actions own destructive API
// calls and user feedback.
async function confirmDanger(message, title) {
  try {
    await ElMessageBox.confirm(message, title, {
      type: "warning",
      confirmButtonText: "删除",
      cancelButtonText: "取消",
      confirmButtonClass: "el-button--danger",
    });
    return true;
  } catch (error) {
    return false;
  }
}

function findJobById(jobs, id) {
  const jobId = Number(id);
  if (!Number.isInteger(jobId) || jobId <= 0) {
    return null;
  }
  return jobs.find((job) => Number(job.id) === jobId) || null;
}

function findJobByStep(jobs, step) {
  const normalizedStep = String(step || "").trim();
  if (!normalizedStep) {
    return null;
  }
  return jobs.find((job) => String(job.step || "").trim() === normalizedStep) || null;
}

function orderedJobs(jobs) {
  return [...jobs].sort((left, right) => {
    const leftStep = Number(left && left.step);
    const rightStep = Number(right && right.step);
    const normalizedLeft = Number.isFinite(leftStep) ? leftStep : Number.MAX_SAFE_INTEGER;
    const normalizedRight = Number.isFinite(rightStep) ? rightStep : Number.MAX_SAFE_INTEGER;
    if (normalizedLeft !== normalizedRight) {
      return normalizedLeft - normalizedRight;
    }
    return Number(left.id || 0) - Number(right.id || 0);
  });
}

function findLoginJob(jobs, preferredId) {
  return findJobById(jobs, preferredId) || orderedJobs(jobs).find((job) => isLoginJob(job)) || null;
}

function findVerifyCodeJob(jobs, preferredId) {
  const preferred = findJobById(jobs, preferredId);
  if (preferred) {
    return preferred;
  }
  const sortedJobs = orderedJobs(jobs);
  // A login name like "手机号验证码登录" also contains "验证码"; prefer the pure
  // verification-code step so the auth flow stays in the intended order.
  return (
    sortedJobs.find((job) => isVerifyCodeJob(job) && !isLoginJob(job)) ||
    sortedJobs.find((job) => isVerifyCodeJob(job)) ||
    null
  );
}

const PHONE_PARAMETER_NAMES = ["phone", "mobilePhone", "mobile", "mobile_phone"];
const IIN_PARAMETER_NAMES = ["iin", "IIN"];

function parseFormObject(value, fieldName) {
  const raw = String(value || "").trim();
  if (!raw) {
    return {};
  }
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error(`${fieldName} 必须是 JSON 对象`);
  }
  return parsed;
}

function readPhoneParameter(source) {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return "";
  }
  const key = PHONE_PARAMETER_NAMES.find((name) => source[name] !== undefined && source[name] !== null);
  return key ? String(source[key]).trim() : "";
}

function applyPhoneParameter(source, phone) {
  const existingKey = PHONE_PARAMETER_NAMES.find((name) => source[name] !== undefined);
  source[existingKey || "phone"] = phone;
}

function removePhoneParameter(source) {
  PHONE_PARAMETER_NAMES.forEach((name) => {
    delete source[name];
  });
}

function applyNamedParameter(sources, names, value, fallbackSource) {
  const target = sources.find((source) => names.some((name) => source[name] !== undefined)) || fallbackSource;
  const existingKey = names.find((name) => target[name] !== undefined);
  target[existingKey || names[0]] = value;
}

function phoneFromJob(job) {
  if (!job) {
    return "";
  }
  const form = formFromJob(job);
  try {
    const params = parseFormObject(form.requestParams, "请求参数");
    const body = parseFormObject(form.requestBody, "请求体");
    return readPhoneParameter(params) || readPhoneParameter(body);
  } catch (error) {
    return "";
  }
}

function formWithPhone(job, phone, baseForm) {
  const form = baseForm ? { ...baseForm } : formFromJob(job);
  const params = parseFormObject(form.requestParams, "请求参数");
  const body = parseFormObject(form.requestBody, "请求体");
  const shouldUseQueryPhone =
    (isVerifyCodeJob(job) && !isLoginJob(job)) || ["GET", "DELETE"].includes(form.http_type);
  if (shouldUseQueryPhone) {
    applyPhoneParameter(params, phone);
    removePhoneParameter(body);
  } else {
    applyPhoneParameter(body, phone);
    removePhoneParameter(params);
  }
  return {
    ...form,
    requestParams: JSON.stringify(params, null, 2),
    requestBody: JSON.stringify(body, null, 2),
  };
}

function formWithIin(jobForm, iin) {
  const form = { ...jobForm };
  const params = parseFormObject(form.requestParams, "请求参数");
  const body = parseFormObject(form.requestBody, "请求体");
  const fallbackSource = ["GET", "DELETE"].includes(form.http_type) ? params : body;
  applyNamedParameter([params, body], IIN_PARAMETER_NAMES, iin, fallbackSource);
  return {
    ...form,
    requestParams: JSON.stringify(params, null, 2),
    requestBody: JSON.stringify(body, null, 2),
  };
}

function formWithSessionCredentials(jobForm, credentials) {
  const form = { ...jobForm };
  const headers = parseFormObject(form.requestHeaders, "请求头");
  headers["Biz-SessionKey"] = credentials.sessionKey;
  headers["Biz-UserId"] = credentials.userId;
  return {
    ...form,
    requestHeaders: JSON.stringify(headers, null, 2),
  };
}

async function executeLoginForCredentials(state, options) {
  const loginJob = findLoginJob(state.jobs, options.loginJobId);
  if (!loginJob) {
    throw new Error("没有找到登录 Job");
  }

  const verifyCodeJob = findVerifyCodeJob(state.jobs, options.verifyJobId);
  const phone =
    String(options.phone || "").trim() ||
    phoneFromJob(loginJob) ||
    phoneFromJob(verifyCodeJob);
  if (!phone) {
    throw new Error("请输入手机号");
  }

  if (verifyCodeJob && Number(verifyCodeJob.id) !== Number(loginJob.id)) {
    const verifyCodeForm =
      Number(state.jobForm.id) === Number(verifyCodeJob.id)
        ? state.jobForm
        : formFromJob(verifyCodeJob);
    await jobApi.update(verifyCodeJob.id, jobPayload(formWithPhone(verifyCodeJob, phone, verifyCodeForm)));
    await jobApi.run(verifyCodeJob.id, false);
  }

  const loginForm =
    Number(state.jobForm.id) === Number(loginJob.id) ? state.jobForm : formFromJob(loginJob);
  await jobApi.update(loginJob.id, jobPayload(formWithPhone(loginJob, phone, loginForm)));
  const loginRun = await jobApi.run(loginJob.id, false);
  const credentials =
    extractLoginCredentials(loginRun.job) ||
    extractLoginCredentials({ last_response: JSON.stringify(loginRun.result || {}) });
  if (!credentials) {
    throw new Error("登录接口没有返回 session/userId");
  }

  return { credentials, loginJob, verifyCodeJob, phone };
}

export default createStore({
  // State is grouped around the main screens: jobs, templates, flows, rules, nodes.
  state: {
    activeView: storedActiveView(),
    authChecked: false,
    authToken: getAuthToken(),
    currentUser: null,
    authMode: "login",
    authenticating: false,
    authForm: {
      username: "",
      password: "",
    },
    httpTypes: HTTP_TYPES,
    dbRuleOperators: DB_RULE_OPERATORS,
    jobs: [],
    jobTemplates: [],
    ruleTemplates: [],
    selectedJob: null,
    allRules: [],
    rules: [],
    flows: [],
    selectedFlow: null,
    databases: [],
    tables: [],
    columns: [],
    ruleJobId: "",
    nodeJobId: "",
    jobRunRuleSelections: {},
    runningJobId: null,
    runningJobStressId: null,
    sharingJobTemplateId: null,
    sharingRuleTemplateId: null,
    copyingJobTemplateId: null,
    copyingRuleTemplateId: null,
    runningFlowId: null,
    runningFlowStressId: null,
    exportingFlowReportId: null,
    jobForm: blankJobForm(),
    ruleForm: blankRuleForm(),
    flowForm: blankFlowForm(),
  },

  getters: {
    isAuthenticated(state) {
      return Boolean(state.currentUser && state.authToken);
    },

    currentResponse(state) {
      return state.selectedJob ? state.selectedJob.last_response || "" : "";
    },

    currentResult(state) {
      if (!state.selectedJob || !state.selectedJob.last_response) {
        return null;
      }
      try {
        return JSON.parse(state.selectedJob.last_response);
      } catch (error) {
        return null;
      }
    },

    currentAssertion(state, getters) {
      return getters.currentResult ? getters.currentResult.assertion || null : null;
    },

    currentFlowResult(state) {
      return state.selectedFlow ? state.selectedFlow.last_result || null : null;
    },
  },

  mutations: {
    setAuthChecked(state, checked) {
      state.authChecked = Boolean(checked);
    },

    setAuthTokenState(state, token) {
      state.authToken = token || "";
    },

    setCurrentUser(state, user) {
      state.currentUser = user || null;
    },

    setAuthMode(state, mode) {
      state.authMode = mode === "register" ? "register" : "login";
    },

    setAuthenticating(state, value) {
      state.authenticating = Boolean(value);
    },

    setAuthFormField(state, { field, value }) {
      if (field in state.authForm) {
        state.authForm[field] = value;
      }
    },

    clearAuthPassword(state) {
      state.authForm.password = "";
    },

    resetWorkspaceState(state) {
      state.jobs = [];
      state.jobTemplates = [];
      state.ruleTemplates = [];
      state.selectedJob = null;
      state.allRules = [];
      state.rules = [];
      state.flows = [];
      state.selectedFlow = null;
      state.databases = [];
      state.tables = [];
      state.columns = [];
      state.ruleJobId = "";
      state.nodeJobId = "";
      state.jobRunRuleSelections = {};
      state.runningJobStressId = null;
      state.sharingJobTemplateId = null;
      state.sharingRuleTemplateId = null;
      state.copyingJobTemplateId = null;
      state.copyingRuleTemplateId = null;
      state.runningFlowStressId = null;
      state.jobForm = blankJobForm();
      state.ruleForm = blankRuleForm();
      state.flowForm = blankFlowForm();
    },

    setActiveView(state, view) {
      const normalizedView = normalizeActiveView(view);
      const nextView = ACTIVE_VIEWS.has(normalizedView) ? normalizedView : "jobs";
      state.activeView = nextView;
      persistActiveView(nextView);
    },

    setJobs(state, jobs) {
      state.jobs = jobs;
    },

    setJobTemplates(state, templates) {
      state.jobTemplates = templates;
    },

    setRuleTemplates(state, templates) {
      state.ruleTemplates = templates;
    },

    setSelectedJob(state, job) {
      state.selectedJob = job;
      if (job) {
        // Selecting a job also primes the rule and node screens for that job.
        const id = String(job.id);
        state.ruleJobId = id;
        state.nodeJobId = id;
        state.jobForm = formFromJob(job);
        if (!state.ruleForm.id) {
          state.ruleForm.jobId = id;
        }
      }
    },

    setRules(state, rules) {
      state.rules = rules;
    },

    setAllRules(state, rules) {
      state.allRules = rules;
    },

    setFlows(state, flows) {
      state.flows = flows;
    },

    setSelectedFlow(state, flow) {
      state.selectedFlow = flow;
      state.flowForm = flow
        ? {
            id: flow.id,
            name: flow.name || "",
            description: flow.description || "",
            steps: Array.isArray(flow.steps)
              ? flow.steps.map((step) => String(step))
              : Array.isArray(flow.jobs)
                ? flow.jobs.map((job) => String(job.step || "")).filter(Boolean)
                : [],
            bindSteps: [],
            afterStep: "",
          }
        : blankFlowForm();
    },

    setDatabases(state, items) {
      state.databases = items;
    },

    setTables(state, items) {
      state.tables = items;
    },

    setColumns(state, items) {
      state.columns = items;
    },

    setRuleJobId(state, id) {
      state.ruleJobId = id;
    },

    setNodeJobId(state, id) {
      state.nodeJobId = id;
    },

    setJobRunWithRules(state, { id, value }) {
      state.jobRunRuleSelections = {
        ...state.jobRunRuleSelections,
        [String(id)]: Boolean(value),
      };
    },

    setRunningJobId(state, id) {
      state.runningJobId = id;
    },

    setRunningJobStressId(state, id) {
      state.runningJobStressId = id;
    },

    setSharingJobTemplateId(state, id) {
      state.sharingJobTemplateId = id;
    },

    setSharingRuleTemplateId(state, id) {
      state.sharingRuleTemplateId = id;
    },

    setCopyingJobTemplateId(state, id) {
      state.copyingJobTemplateId = id;
    },

    setCopyingRuleTemplateId(state, id) {
      state.copyingRuleTemplateId = id;
    },

    setRunningFlowId(state, id) {
      state.runningFlowId = id;
    },

    setRunningFlowStressId(state, id) {
      state.runningFlowStressId = id;
    },

    setExportingFlowReportId(state, id) {
      state.exportingFlowReportId = id;
    },

    setJobFormField(state, { field, value }) {
      state.jobForm[field] = value;
    },

    setRuleFormField(state, { field, value }) {
      state.ruleForm[field] = value;
    },

    setRuleAssertionField(state, { index, field, value }) {
      if (!state.ruleForm.assertions[index]) {
        return;
      }
      state.ruleForm.assertions[index][field] = value;
    },

    addRuleAssertion(state, field = "") {
      const exists = state.ruleForm.assertions.some((assertion) => assertion.field === field && field);
      if (exists) {
        return;
      }
      state.ruleForm.assertions.push(blankRuleAssertion(field));
    },

    removeRuleAssertion(state, index) {
      state.ruleForm.assertions.splice(index, 1);
      if (!state.ruleForm.assertions.length) {
        state.ruleForm.assertions.push(blankRuleAssertion());
      }
    },

    setFlowFormField(state, { field, value }) {
      state.flowForm[field] = value;
    },

    setFlowSteps(state, steps) {
      state.flowForm.steps = normalizeSteps(steps);
    },

    clearFlowBindFields(state) {
      state.flowForm.bindSteps = [];
      state.flowForm.afterStep = "";
    },

    setRuleForm(state, rule) {
      state.ruleForm = ruleFormFromRule(rule);
    },

    resetJobForm(state) {
      state.selectedJob = null;
      state.rules = [];
      state.ruleJobId = "";
      state.nodeJobId = "";
      state.jobForm = blankJobForm();
      state.ruleForm = blankRuleForm();
    },

    setJobFormFromCurl(state, job) {
      state.selectedJob = null;
      state.rules = [];
      state.ruleJobId = "";
      state.nodeJobId = "";
      state.ruleForm = blankRuleForm();
      state.jobForm = formFromCurlJob(job, state.jobForm);
    },

    resetRuleForm(state) {
      state.ruleForm = blankRuleForm();
    },

    resetFlowForm(state) {
      state.selectedFlow = null;
      state.flowForm = blankFlowForm();
    },

  },

  actions: {
    showToast(context, { message, isError = false }) {
      ElMessage({
        message,
        type: isError ? "error" : "success",
        duration: isError ? 3200 : 2200,
      });
    },

    async loadSession({ commit, state }) {
      if (!state.authToken) {
        commit("setAuthChecked", true);
        return false;
      }
      try {
        const data = await authApi.me();
        commit("setCurrentUser", data.user || null);
        commit("setAuthChecked", true);
        return Boolean(data.user);
      } catch (error) {
        setAuthToken("");
        commit("setAuthTokenState", "");
        commit("setCurrentUser", null);
        commit("resetWorkspaceState");
        commit("setAuthChecked", true);
        return false;
      }
    },

    async submitAuth({ commit, dispatch, state }, mode) {
      const nextMode = mode || state.authMode;
      commit("setAuthenticating", true);
      try {
        const payload = {
          username: state.authForm.username,
          password: state.authForm.password,
        };
        const data = nextMode === "register"
          ? await authApi.register(payload)
          : await authApi.login(payload);
        setAuthToken(data.token || "");
        commit("setAuthTokenState", data.token || "");
        commit("setCurrentUser", data.user || null);
        commit("setAuthChecked", true);
        commit("clearAuthPassword");
        dispatch("showToast", { message: nextMode === "register" ? "注册成功" : "登录成功" });
        await dispatch("loadJobs");
        await dispatch("loadJobTemplates");
        await dispatch("loadRuleTemplates");
        await dispatch("loadAllRules");
        await dispatch("loadFlows");
        await dispatch("loadDatabases");
        return true;
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
        return false;
      } finally {
        commit("setAuthenticating", false);
      }
    },

    async logout({ commit }) {
      try {
        await authApi.logout();
      } catch (error) {
        // Clearing the local token is enough if the server session already expired.
      }
      setAuthToken("");
      commit("setAuthTokenState", "");
      commit("setCurrentUser", null);
      commit("resetWorkspaceState");
      commit("setAuthChecked", true);
    },

    async loadDatabases({ commit, dispatch }) {
      try {
        const data = await dbmetaApi.listDatabases();
        commit("setDatabases", data.items || []);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async loadTables({ commit, dispatch }, database) {
      commit("setTables", []);
      commit("setColumns", []);
      if (!database) {
        return;
      }
      try {
        const data = await dbmetaApi.listTables(database);
        commit("setTables", data.items || []);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async loadColumns({ commit, dispatch }, { database, table }) {
      commit("setColumns", []);
      if (!database || !table) {
        return;
      }
      try {
        const data = await dbmetaApi.listColumns(database, table);
        commit("setColumns", data.items || []);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async loadJobs({ commit, state, dispatch }) {
      try {
        const data = await jobApi.list();
        commit("setJobs", data.items);
        if (state.selectedJob) {
          const exists = data.items.some((job) => job.id === state.selectedJob.id);
          if (!exists) {
            commit("resetJobForm");
          }
        }
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async loadJobTemplates({ commit, dispatch }) {
      try {
        const data = await jobApi.listTemplates();
        commit("setJobTemplates", data.items || []);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async loadRuleTemplates({ commit, dispatch }) {
      try {
        const data = await jobApi.listRuleTemplates();
        commit("setRuleTemplates", data.items || []);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async loadAllRules({ commit, dispatch }) {
      try {
        const data = await ruleApi.listAll();
        commit("setAllRules", data.items || []);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async selectJob({ commit, dispatch }, id) {
      try {
        const job = await jobApi.get(id);
        commit("setSelectedJob", job);
        commit("setRules", job.testrules || []);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    switchView({ commit, state }, view) {
      commit("setActiveView", view);
      if (view === "nodes" && state.selectedJob) {
        commit("setNodeJobId", String(state.selectedJob.id));
      }
    },

    async loadFlows({ commit, dispatch }) {
      try {
        const data = await flowApi.list();
        commit("setFlows", data.items || []);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async selectFlow({ commit, dispatch }, id) {
      try {
        const flow = await flowApi.get(id);
        commit("setSelectedFlow", flow);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async selectNodeJob({ commit, dispatch }, id) {
      commit("setActiveView", "nodes");
      commit("setNodeJobId", id || "");
      if (!id) {
        commit("setSelectedJob", null);
        return;
      }
      await dispatch("selectJob", Number(id));
    },

    async selectRuleJob({ commit, dispatch }, id) {
      commit("setRuleJobId", id || "");
      if (!id) {
        commit("setSelectedJob", null);
        commit("setRules", []);
        commit("resetRuleForm");
        return;
      }
      commit("resetRuleForm");
      await dispatch("selectJob", Number(id));
      commit("setRuleFormField", { field: "jobId", value: String(id) });
    },

    async saveJob({ state, dispatch }) {
      try {
        const id = state.jobForm.id;
        const payload = jobPayload(state.jobForm);
        const saved = id ? await jobApi.update(id, payload) : await jobApi.create(payload);
        dispatch("showToast", { message: "TestJob 已保存" });
        await dispatch("loadJobs");
        await dispatch("selectJob", saved.id);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async parseCurlJob({ commit, dispatch }, curlCommand) {
      try {
        const data = await jobApi.parseCurl(curlCommand);
        commit("setJobFormFromCurl", data.job || data);
        dispatch("showToast", { message: "cURL 已解析" });
        return true;
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
        return false;
      }
    },

    async deleteJob({ commit, state, dispatch }) {
      if (!state.jobForm.id) {
        return;
      }
      const confirmed = await confirmDanger("确认删除该 TestJob 及其全部规则？", "删除 TestJob");
      if (!confirmed) {
        return;
      }
      try {
        await jobApi.remove(state.jobForm.id);
        dispatch("showToast", { message: "TestJob 已删除" });
        commit("resetJobForm");
        await dispatch("loadJobs");
        await dispatch("loadAllRules");
        await dispatch("loadFlows");
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async copyJob({ dispatch }, id) {
      try {
        const job = await jobApi.copy(id);
        dispatch("showToast", { message: `Job 已复制为 Step ${job.step}` });
        await dispatch("loadJobs");
        await dispatch("selectJob", job.id);
        await dispatch("loadAllRules");
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async shareJobTemplate({ commit, dispatch }, id) {
      const jobId = Number(id);
      if (!Number.isInteger(jobId) || jobId <= 0) {
        dispatch("showToast", { message: "请选择要分享的 Job", isError: true });
        return false;
      }

      commit("setSharingJobTemplateId", jobId);
      try {
        await jobApi.shareTemplate(jobId);
        dispatch("showToast", { message: "Job 模板已分享" });
        await dispatch("loadJobTemplates");
        await dispatch("loadRuleTemplates");
        return true;
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
        return false;
      } finally {
        commit("setSharingJobTemplateId", null);
      }
    },

    async copyJobTemplate({ commit, dispatch }, id) {
      const templateId = Number(id);
      if (!Number.isInteger(templateId) || templateId <= 0) {
        dispatch("showToast", { message: "请选择要导入的模板", isError: true });
        return false;
      }

      commit("setCopyingJobTemplateId", templateId);
      try {
        const job = await jobApi.copyTemplate(templateId);
        dispatch("showToast", { message: `模板已导入为 Step ${job.step}` });
        await dispatch("loadJobs");
        await dispatch("selectJob", job.id);
        await dispatch("loadAllRules");
        return true;
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
        return false;
      } finally {
        commit("setCopyingJobTemplateId", null);
      }
    },

    async copyRuleTemplate({ commit, dispatch }, payload) {
      const options = payload && typeof payload === "object" ? payload : {};
      const ruleTemplateId = Number(options.id);
      const targetJobId = Number(options.jobId);
      if (!Number.isInteger(ruleTemplateId) || ruleTemplateId <= 0) {
        dispatch("showToast", { message: "请选择要导入的 Rule", isError: true });
        return false;
      }
      if (!Number.isInteger(targetJobId) || targetJobId <= 0) {
        dispatch("showToast", { message: "请选择导入到哪个 Job", isError: true });
        return false;
      }

      commit("setCopyingRuleTemplateId", ruleTemplateId);
      try {
        await jobApi.copyRuleTemplate(ruleTemplateId, targetJobId);
        dispatch("showToast", { message: "Rule 已导入" });
        await dispatch("selectJob", targetJobId);
        await dispatch("loadJobs");
        await dispatch("loadAllRules");
        return true;
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
        return false;
      } finally {
        commit("setCopyingRuleTemplateId", null);
      }
    },

    async shareRuleTemplate({ commit, dispatch }, id) {
      const ruleId = Number(id);
      if (!Number.isInteger(ruleId) || ruleId <= 0) {
        dispatch("showToast", { message: "请选择要分享的 Rule", isError: true });
        return false;
      }

      commit("setSharingRuleTemplateId", ruleId);
      try {
        await ruleApi.shareTemplate(ruleId);
        dispatch("showToast", { message: "Rule 已分享" });
        await dispatch("loadRuleTemplates");
        return true;
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
        return false;
      } finally {
        commit("setSharingRuleTemplateId", null);
      }
    },

    async runJob({ commit, dispatch }, payload) {
      const options =
        payload && typeof payload === "object" ? payload : { id: payload };
      const id = Number(options.id);
      const includeRules =
        options.includeRules === undefined ? true : Boolean(options.includeRules);
      commit("setRunningJobId", id);
      try {
        const data = await jobApi.run(id, includeRules);
        // Refresh the selected job because the backend persists last_response.
        commit("setSelectedJob", data.job);
        commit("setRules", data.job.testrules || []);
        await dispatch("loadJobs");
        dispatch("showToast", {
          message: includeRules ? "节点测试完成，已测试绑定规则" : "节点测试完成",
        });
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      } finally {
        commit("setRunningJobId", null);
      }
    },

    async stressJob({ commit, dispatch }, payload) {
      const options = payload && typeof payload === "object" ? payload : { id: payload };
      const id = Number(options.id);
      if (!Number.isInteger(id) || id <= 0) {
        dispatch("showToast", { message: "请选择要压测的 Job", isError: true });
        return null;
      }

      commit("setRunningJobStressId", id);
      try {
        const data = await jobApi.stress(id, {
          threads: options.threads,
          rounds: options.rounds,
          include_rules: options.includeRules,
        });
        dispatch("showToast", { message: "Job 压测已开始" });
        return data.task || null;
      } catch (error) {
        commit("setRunningJobStressId", null);
        dispatch("showToast", { message: error.message, isError: true });
        return null;
      }
    },

    async fetchStressTask({ dispatch }, taskId) {
      try {
        const data = await stressApi.get(taskId);
        return data.task || null;
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
        return null;
      }
    },

    async runSelectedNode({ state, dispatch }) {
      if (!state.nodeJobId) {
        dispatch("showToast", { message: "请先选择节点", isError: true });
        return;
      }
      await dispatch("runJob", Number(state.nodeJobId));
    },

    async saveFlow({ commit, state, dispatch }) {
      try {
        const id = state.flowForm.id;
        const steps = mergeFlowSteps(
          state.flowForm.steps,
          state.flowForm.bindSteps,
          state.flowForm.afterStep,
        );
        const payload = flowPayload({ ...state.flowForm, steps });
        const saved = id ? await flowApi.update(id, payload) : await flowApi.create(payload);
        dispatch("showToast", { message: "流程已保存" });
        commit("clearFlowBindFields");
        await dispatch("loadFlows");
        await dispatch("selectFlow", saved.id);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async deleteFlow({ commit, state, dispatch }) {
      if (!state.flowForm.id) {
        return;
      }
      const confirmed = await confirmDanger("确认删除该流程？", "删除流程");
      if (!confirmed) {
        return;
      }
      try {
        await flowApi.remove(state.flowForm.id);
        dispatch("showToast", { message: "流程已删除" });
        commit("resetFlowForm");
        await dispatch("loadFlows");
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async bindFlowJob({ commit, state, dispatch }) {
      const steps = normalizeSteps(state.flowForm.bindSteps);
      if (!steps.length) {
        dispatch("showToast", { message: "请选择要添加的 Step", isError: true });
        return;
      }

      if (!state.flowForm.id) {
        commit("setFlowSteps", mergeFlowSteps(state.flowForm.steps, steps, state.flowForm.afterStep));
        commit("clearFlowBindFields");
        dispatch("showToast", { message: "Step 已加入流程，保存后生效" });
        return;
      }

      try {
        const saved = await flowApi.bind(state.flowForm.id, {
          steps,
          after_step: state.flowForm.afterStep || null,
        });
        dispatch("showToast", { message: "Step 已添加到流程" });
        commit("clearFlowBindFields");
        await dispatch("loadFlows");
        await dispatch("selectFlow", saved.id);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async fillJobSessionHeaders({ state, dispatch }, payload) {
      const options = payload && typeof payload === "object" ? payload : { jobId: payload };
      const targetJob =
        findJobById(state.jobs, options.jobId) ||
        (Number(state.selectedJob?.id) === Number(options.jobId) ? state.selectedJob : null);

      if (!targetJob) {
        dispatch("showToast", { message: "请选择要填充的 Job", isError: true });
        return false;
      }

      try {
        const { credentials, verifyCodeJob, loginJob, phone } = await executeLoginForCredentials(state, options);

        let form =
          Number(state.jobForm.id) === Number(targetJob.id)
            ? { ...state.jobForm }
            : formFromJob(targetJob);
        if ([verifyCodeJob?.id, loginJob.id].some((id) => Number(id) === Number(targetJob.id))) {
          form = formWithPhone(targetJob, phone, form);
        }
        await jobApi.update(targetJob.id, jobPayload(formWithSessionCredentials(form, credentials)));
        dispatch("showToast", { message: `已执行登录并填充 userId ${credentials.userId}` });
        await dispatch("loadJobs");
        await dispatch("selectJob", targetJob.id);
        return true;
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
        return false;
      }
    },

    async fillFlowSessionHeaders({ state, dispatch }, payload) {
      const options = payload && typeof payload === "object" ? payload : {};
      const targetJobs = [];
      const targetIds = new Set();
      const addTargetJob = (job) => {
        if (!job || job.missing || isVerifyCodeJob(job) || isLoginJob(job) || targetIds.has(Number(job.id))) {
          return;
        }
        targetIds.add(Number(job.id));
        targetJobs.push(job);
      };

      (options.jobIds || []).forEach((id) => addTargetJob(findJobById(state.jobs, id)));
      (options.steps || state.flowForm.steps || []).forEach((step) => addTargetJob(findJobByStep(state.jobs, step)));

      if (!targetJobs.length) {
        dispatch("showToast", { message: "流程里没有需要填充的业务接口", isError: true });
        return false;
      }

      try {
        const { credentials } = await executeLoginForCredentials(state, options);
        for (const job of targetJobs) {
          const form = Number(state.jobForm.id) === Number(job.id) ? { ...state.jobForm } : formFromJob(job);
          await jobApi.update(job.id, jobPayload(formWithSessionCredentials(form, credentials)));
        }

        await dispatch("loadJobs");
        await dispatch("loadFlows");
        const flowId = options.flowId || state.selectedFlow?.id || state.flowForm.id;
        if (flowId) {
          await dispatch("selectFlow", flowId);
        }
        dispatch("showToast", {
          message: `已填充 ${targetJobs.length} 个接口 userId ${credentials.userId}`,
        });
        return true;
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
        await dispatch("loadJobs");
        return false;
      }
    },

    async fillJobIin({ commit, state, dispatch }) {
      try {
        const data = await toolsApi.generateIin();
        const iin = String(data.iin || "").trim();
        if (!iin) {
          dispatch("showToast", { message: "iin 接口没有返回 iin", isError: true });
          return false;
        }

        const form = formWithIin(state.jobForm, iin);
        commit("setJobFormField", { field: "requestParams", value: form.requestParams });
        commit("setJobFormField", { field: "requestBody", value: form.requestBody });
        if (form.id) {
          await jobApi.update(form.id, jobPayload(form));
          await dispatch("loadJobs");
          await dispatch("selectJob", form.id);
        }
        dispatch("showToast", { message: `已填充 iin ${iin}` });
        return true;
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
        return false;
      }
    },

    async runFlow({ commit, dispatch }, id) {
      commit("setRunningFlowId", id);
      try {
        const data = await flowApi.run(id);
        dispatch("showToast", { message: data.result.ok ? "流程执行完成" : "流程执行失败" });
        await dispatch("loadJobs");
        await dispatch("loadFlows");
        await dispatch("selectFlow", data.flow.id);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      } finally {
        commit("setRunningFlowId", null);
      }
    },

    async stressFlow({ commit, state, dispatch }, payload) {
      const options = payload && typeof payload === "object" ? payload : { id: payload };
      const flowId = Number(options.id || state.flowForm.id || state.selectedFlow?.id);
      if (!Number.isInteger(flowId) || flowId <= 0) {
        dispatch("showToast", { message: "请选择要压测的流程", isError: true });
        return null;
      }

      commit("setRunningFlowStressId", flowId);
      try {
        const data = await flowApi.stress(flowId, {
          threads: options.threads,
          rounds: options.rounds,
          include_rules: options.includeRules,
        });
        dispatch("showToast", { message: "流程压测已开始" });
        return data.task || null;
      } catch (error) {
        commit("setRunningFlowStressId", null);
        dispatch("showToast", { message: error.message, isError: true });
        return null;
      }
    },

    async exportFlowReport({ commit, state, dispatch }, id) {
      const flowId = Number(id || state.flowForm.id || state.selectedFlow?.id);
      if (!Number.isInteger(flowId) || flowId <= 0) {
        dispatch("showToast", { message: "请选择要导出的流程", isError: true });
        return;
      }

      commit("setExportingFlowReportId", flowId);
      try {
        const report = await flowApi.exportReport(flowId);
        downloadTextFile(report.filename, report.content);
        dispatch("showToast", { message: "测试报告已导出" });
        await dispatch("loadFlows");
        await dispatch("selectFlow", flowId);
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      } finally {
        commit("setExportingFlowReportId", null);
      }
    },

    async saveRule({ commit, state, dispatch }) {
      const boundJobId = Number(state.ruleForm.jobId || state.ruleJobId || state.selectedJob?.id);
      if (!Number.isInteger(boundJobId) || boundJobId <= 0) {
        dispatch("showToast", { message: "请选择绑定 Job", isError: true });
        return;
      }
      try {
        const id = state.ruleForm.id;
        const ruleText = buildRuleText(state.ruleForm);
        if (!ruleText) {
          dispatch("showToast", { message: "请输入规则内容", isError: true });
          return;
        }
        const payload = { rule: ruleText, job_id: boundJobId };
        if (id) {
          await ruleApi.update(id, payload);
        } else {
          await ruleApi.create(boundJobId, payload);
        }
        dispatch("showToast", { message: "规则已保存" });
        commit("resetRuleForm");
        await dispatch("selectJob", boundJobId);
        await dispatch("loadJobs");
        await dispatch("loadAllRules");
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },

    async deleteRule({ commit, state, dispatch }, id) {
      const confirmed = await confirmDanger("确认删除该规则？", "删除规则");
      if (!confirmed) {
        return;
      }
      const rule = [...state.rules, ...state.allRules].find((item) => Number(item.id) === Number(id));
      const selectedJobId = state.selectedJob ? state.selectedJob.id : Number(rule?.job_id);
      try {
        await ruleApi.remove(id);
        dispatch("showToast", { message: "规则已删除" });
        if (Number(state.ruleForm.id) === Number(id)) {
          commit("resetRuleForm");
        }
        if (selectedJobId) {
          await dispatch("selectJob", selectedJobId);
        } else {
          commit("setRules", []);
        }
        await dispatch("loadJobs");
        await dispatch("loadAllRules");
      } catch (error) {
        dispatch("showToast", { message: error.message, isError: true });
      }
    },
  },
});
